import * as React from 'react';

import { Button, CircularProgress, InputBase, Typography } from '@mui/material';
import {
  type AskClarifyingQuestionsOutput,
  CLARIFY_LIMITS,
  type ClarifyingQuestion,
} from '@realadvisor/common-types';
import {
  ArrowLeft,
  Check,
  CircleAlert,
  CircleHelp,
  PenLine,
} from 'lucide-react';

import { type ToolPart } from '@modules/_shared/components/chat/ApprovalActions';
import {
  type AnswerMap,
  type ClarifyCardMode,
  type ClarifyResolveHandler,
  type QuestionAnswer,
  buildClarifyOutput,
  clarifyCardMode,
  clarifyErrorText,
  clarifySummaryRows,
  emptyAnswer,
  hasAnswer,
  invalidInputOutput,
  sanitizeQuestions,
  skippedOutput,
} from '@modules/_shared/components/chat/clarifyAnswers';
import { useLocale } from '@shared/hooks/locale';
import { useTheme } from '@shared/theme/theme';

// UI for the client-side `ask_clarifying_questions` tool: the agent's turn is
// paused until the user answers or skips (addToolResult, then
// sendAutomaticallyWhen resumes the agent). The questions are answered in
// ClarifyingQuestionsDock, docked above the chat input, one per screen; the
// transcript keeps ClarifyingQuestionsCard, a status row then a summary.
// Prompts and options are model text and are always rendered as plain text.

type Colors = ReturnType<typeof useTheme>['colors'];

const AUTO_ADVANCE_MS = 250;

const titleOf = (input: unknown): string | null => {
  if (typeof input !== 'object' || input == null) {
    return null;
  }
  const title = (input as { title?: unknown }).title;
  return typeof title === 'string' && title.trim().length > 0
    ? title.trim()
    : null;
};

const SelectIndicator = ({
  selected,
  multiple,
  colors,
}: {
  selected: boolean;
  multiple: boolean;
  colors: Colors;
}) => (
  <span
    css={{
      width: 16,
      height: 16,
      flexShrink: 0,
      borderRadius: multiple ? 4 : '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.15s, border-color 0.15s',
      border: `1.5px solid ${selected ? colors.primaryMain : colors.grey400}`,
      backgroundColor: selected ? colors.primaryMain : colors.white,
      color: colors.white,
    }}
  >
    {multiple
      ? selected && <Check size={11} strokeWidth={3} />
      : selected && (
          <span
            css={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: colors.white,
            }}
          />
        )}
  </span>
);

const optionRowCss = (colors: Colors, selected: boolean) =>
  ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    minHeight: 32,
    padding: '5px 8px',
    border: 'none',
    borderRadius: 6,
    background: selected ? colors.blue50 : 'none',
    cursor: 'pointer',
    textAlign: 'left',
    font: 'inherit',
    color: colors.grey800,
    transition: 'background-color 0.1s',
    '&:hover': { backgroundColor: selected ? colors.blue50 : colors.grey100 },
    '&:focus-visible': {
      outline: `2px solid ${colors.primaryLight}`,
      outlineOffset: -2,
    },
  } as const);

const OptionRow = ({
  label,
  selected,
  multiple,
  recommended,
  onToggle,
  colors,
}: {
  label: string;
  selected: boolean;
  multiple: boolean;
  recommended: boolean;
  onToggle: () => void;
  colors: Colors;
}) => {
  const { t } = useLocale();
  return (
    <button
      type="button"
      role={multiple ? 'checkbox' : 'radio'}
      aria-checked={selected}
      onClick={onToggle}
      css={optionRowCss(colors, selected)}
    >
      <SelectIndicator
        selected={selected}
        multiple={multiple}
        colors={colors}
      />
      <Typography
        variant="body2"
        sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}
      >
        {label}
      </Typography>
      {recommended && (
        <span
          css={{
            flexShrink: 0,
            padding: '1px 6px',
            borderRadius: 999,
            backgroundColor: colors.blue100,
            color: colors.blue700,
            fontSize: '0.65rem',
            fontWeight: 600,
          }}
        >
          {t('Recommended')}
        </span>
      )}
    </button>
  );
};

const StatusPill = ({
  mode,
  colors,
}: {
  mode: Exclude<ClarifyCardMode, 'pending'>;
  colors: Colors;
}) => {
  const { t } = useLocale();
  const palette =
    mode === 'answered'
      ? { bg: colors.green100, fg: colors.green700 }
      : mode === 'error'
      ? { bg: colors.red100, fg: colors.red700 }
      : mode === 'interactive'
      ? { bg: colors.blue100, fg: colors.blue700 }
      : { bg: colors.grey100, fg: colors.grey700 };
  return (
    <span
      css={{
        marginLeft: 'auto',
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '1px 8px',
        borderRadius: 999,
        fontSize: '0.7rem',
        fontWeight: 600,
        backgroundColor: palette.bg,
        color: palette.fg,
      }}
    >
      {mode === 'answered' && <Check size={11} strokeWidth={3} />}
      {mode === 'error' && <CircleAlert size={11} />}
      {mode === 'answered'
        ? t('Answered')
        : mode === 'skipped'
        ? t('Skipped')
        : mode === 'notAnswered'
        ? t('Not answered')
        : mode === 'interactive'
        ? t('Waiting for your answer')
        : t('Failed')}
    </span>
  );
};

const PagerDots = ({
  count,
  step,
  onGo,
  colors,
}: {
  count: number;
  step: number;
  onGo: (index: number) => void;
  colors: Colors;
}) => {
  const { t } = useLocale();
  return (
    <span css={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      {Array.from({ length: count }, (_, index) => (
        <button
          key={index}
          type="button"
          aria-label={t('Question {{current}} of {{total}}', {
            current: index + 1,
            total: count,
          })}
          aria-current={index === step ? 'step' : undefined}
          onClick={() => onGo(index)}
          css={{
            width: index === step ? 9 : 7,
            height: index === step ? 9 : 7,
            padding: 0,
            borderRadius: '50%',
            cursor: 'pointer',
            transition: 'all 0.2s',
            border:
              index === step
                ? `2.5px solid ${colors.primaryMain}`
                : index < step
                ? 'none'
                : `1.5px solid ${colors.grey400}`,
            backgroundColor: index < step ? colors.grey500 : 'transparent',
          }}
        />
      ))}
    </span>
  );
};

const QuestionStep = ({
  question,
  answer,
  onToggleOption,
  onToggleOther,
  onChange,
  onEnter,
  colors,
}: {
  question: ClarifyingQuestion;
  answer: QuestionAnswer;
  onToggleOption: (option: string) => void;
  onToggleOther: () => void;
  onChange: (patch: Partial<QuestionAnswer>) => void;
  onEnter: () => void;
  colors: Colors;
}) => {
  const { t } = useLocale();
  const multiple = question.allowMultiple === true;
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      onEnter();
    }
  };
  const inputSx = {
    flex: 1,
    minWidth: 0,
    p: 0,
    fontSize: '0.875rem',
    '& .MuiInputBase-input': { p: 0 },
  } as const;
  return (
    <div>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          color: colors.grey900,
          px: 1,
          pb: 0.75,
          wordBreak: 'break-word',
        }}
      >
        {question.prompt}
      </Typography>
      {question.type === 'text' ? (
        <div
          css={{
            margin: '0 8px 4px',
            padding: '6px 10px',
            borderRadius: 6,
            border: `1px solid ${colors.divider}`,
            backgroundColor: colors.white,
          }}
        >
          <InputBase
            autoFocus
            fullWidth
            multiline
            minRows={1}
            maxRows={4}
            placeholder={t('Type something…')}
            value={answer.text}
            onChange={event => onChange({ text: event.target.value })}
            onKeyDown={handleKeyDown}
            inputProps={{ maxLength: CLARIFY_LIMITS.maxAnswerLength }}
            sx={inputSx}
          />
        </div>
      ) : (
        <div
          role={multiple ? 'group' : 'radiogroup'}
          aria-label={question.prompt}
        >
          {multiple && (
            <Typography
              variant="caption"
              sx={{ display: 'block', px: 1, pb: 0.5, color: colors.grey500 }}
            >
              {t('Select all that apply')}
            </Typography>
          )}
          {(question.options ?? []).map(option => (
            <OptionRow
              key={option}
              label={option}
              selected={answer.selected.includes(option)}
              multiple={multiple}
              recommended={!multiple && question.recommendedOption === option}
              onToggle={() => onToggleOption(option)}
              colors={colors}
            />
          ))}
          {question.allowOther !== false &&
            (answer.otherSelected ? (
              <div
                css={{
                  ...optionRowCss(colors, true),
                  cursor: 'text',
                }}
              >
                <button
                  type="button"
                  aria-label={t('Other…')}
                  aria-pressed
                  onClick={onToggleOther}
                  css={{
                    display: 'flex',
                    padding: 0,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    color: colors.primaryMain,
                    flexShrink: 0,
                  }}
                >
                  <PenLine size={15} />
                </button>
                <InputBase
                  autoFocus
                  fullWidth
                  multiline
                  maxRows={3}
                  placeholder={t('Type something…')}
                  value={answer.otherText}
                  onChange={event =>
                    onChange({ otherText: event.target.value })
                  }
                  onKeyDown={event => {
                    if (event.key === 'Escape') {
                      event.preventDefault();
                      onToggleOther();
                      return;
                    }
                    handleKeyDown(event);
                  }}
                  inputProps={{ maxLength: CLARIFY_LIMITS.maxAnswerLength }}
                  sx={inputSx}
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={onToggleOther}
                css={optionRowCss(colors, false)}
              >
                <span
                  css={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: 16,
                    flexShrink: 0,
                    color: colors.grey500,
                  }}
                >
                  <PenLine size={15} />
                </span>
                <Typography variant="body2" sx={{ color: colors.grey600 }}>
                  {t('Other…')}
                </Typography>
              </button>
            ))}
        </div>
      )}
    </div>
  );
};

const InteractiveBody = ({
  questions,
  onResolve,
  colors,
}: {
  questions: ReadonlyArray<ClarifyingQuestion>;
  onResolve: (output: AskClarifyingQuestionsOutput) => void;
  colors: Colors;
}) => {
  const { t } = useLocale();
  const [answers, setAnswers] = React.useState<AnswerMap>({});
  const [step, setStep] = React.useState(0);
  const advanceTimerRef = React.useRef<number | null>(null);
  const lastStep = Math.max(questions.length - 1, 0);
  const currentStep = Math.min(step, lastStep);
  const question = questions[currentStep];
  const isLast = currentStep >= lastStep;

  const cancelScheduledAdvance = () => {
    if (advanceTimerRef.current != null) {
      window.clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
  };
  React.useEffect(
    () => () => {
      if (advanceTimerRef.current != null) {
        window.clearTimeout(advanceTimerRef.current);
      }
    },
    [],
  );

  if (question == null) {
    return null;
  }
  const answer = answers[question.id] ?? emptyAnswer();
  const update = (patch: Partial<QuestionAnswer>) =>
    setAnswers(prev => ({
      ...prev,
      [question.id]: { ...(prev[question.id] ?? emptyAnswer()), ...patch },
    }));
  const goTo = (index: number) => {
    cancelScheduledAdvance();
    setStep(Math.min(Math.max(index, 0), lastStep));
  };
  const submit = () => {
    cancelScheduledAdvance();
    onResolve(buildClarifyOutput(questions, answers));
  };
  const next = () => (isLast ? submit() : goTo(currentStep + 1));

  const toggleOption = (option: string) => {
    if (question.allowMultiple === true) {
      update({
        selected: answer.selected.includes(option)
          ? answer.selected.filter(value => value !== option)
          : [...answer.selected, option],
      });
      return;
    }
    const deselecting = answer.selected.includes(option);
    update({ selected: deselecting ? [] : [option], otherSelected: false });
    // Let the selection show before the screen changes; the last question
    // still needs an explicit Send.
    if (!deselecting && !isLast) {
      cancelScheduledAdvance();
      advanceTimerRef.current = window.setTimeout(() => {
        advanceTimerRef.current = null;
        setStep(prev => Math.min(prev + 1, lastStep));
      }, AUTO_ADVANCE_MS);
    }
  };
  const toggleOther = () => {
    cancelScheduledAdvance();
    update({
      otherSelected: !answer.otherSelected,
      ...(question.allowMultiple === true || answer.otherSelected
        ? {}
        : { selected: [] }),
    });
  };

  return (
    <>
      {questions.length > 1 && (
        <Typography
          variant="caption"
          sx={{ display: 'block', px: 1, pb: 0.25, color: colors.grey500 }}
        >
          {t('Question {{current}} of {{total}}', {
            current: currentStep + 1,
            total: questions.length,
          })}
        </Typography>
      )}
      <QuestionStep
        key={question.id}
        question={question}
        answer={answer}
        onToggleOption={toggleOption}
        onToggleOther={toggleOther}
        onChange={update}
        onEnter={next}
        colors={colors}
      />
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 8px 2px',
        }}
      >
        {questions.length > 1 && (
          <>
            <Button
              size="small"
              variant="text"
              color="inherit"
              disabled={currentStep === 0}
              startIcon={<ArrowLeft size={14} />}
              onClick={() => goTo(currentStep - 1)}
              sx={{ color: colors.grey600, minWidth: 0 }}
            >
              {t('Back')}
            </Button>
            <PagerDots
              count={questions.length}
              step={currentStep}
              onGo={goTo}
              colors={colors}
            />
          </>
        )}
        <div css={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <Button
            size="small"
            variant="text"
            color="inherit"
            onClick={() => {
              cancelScheduledAdvance();
              onResolve(skippedOutput());
            }}
            sx={{ color: colors.grey600 }}
          >
            {t('Skip')}
          </Button>
          <Button
            size="small"
            variant={hasAnswer(question, answer) ? 'contained' : 'outlined'}
            disableElevation
            onClick={next}
          >
            {isLast ? t('Send') : t('Next')}
          </Button>
        </div>
      </div>
    </>
  );
};

const CardHeader = ({
  title,
  status,
  compact = false,
  colors,
}: {
  title: string;
  status?: React.ReactNode;
  compact?: boolean;
  colors: Colors;
}) => (
  <div
    css={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: compact ? '2px 8px' : '2px 8px 6px',
      color: colors.grey600,
    }}
  >
    <CircleHelp size={15} style={{ flexShrink: 0 }} />
    <Typography
      variant="body2"
      sx={{ fontWeight: 500, flex: 1, minWidth: 0, color: colors.grey700 }}
      noWrap
    >
      {title}
    </Typography>
    {status}
  </div>
);

// The answerable card, rendered by ChatPanelInner between the transcript and
// the chat input while the last turn waits on this call. Remount it per call
// (key it on toolCallId) so each call starts on its first question.
export const ClarifyingQuestionsDock = ({
  part,
  onClarifyResolve,
}: {
  part: ToolPart;
  onClarifyResolve: ClarifyResolveHandler;
}) => {
  const { colors } = useTheme();
  const { t } = useLocale();
  const questions = React.useMemo(
    () => sanitizeQuestions(part.input),
    [part.input],
  );
  const toolCallId = part.toolCallId;

  const resolvedRef = React.useRef(false);
  const resolve = React.useCallback(
    (output: AskClarifyingQuestionsOutput) => {
      if (resolvedRef.current || toolCallId == null) {
        return;
      }
      resolvedRef.current = true;
      onClarifyResolve(toolCallId, output);
    },
    [onClarifyResolve, toolCallId],
  );

  // Nothing renderable: fail the call so the agent can retry with valid input.
  const hasNoQuestion = questions.length === 0;
  React.useEffect(() => {
    if (hasNoQuestion) {
      resolve(invalidInputOutput());
    }
  }, [hasNoQuestion, resolve]);

  // Move focus from the chat input to the first question, unless the user is
  // typing a draft there (typing instead of answering is allowed).
  const dockRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const dock = dockRef.current;
    const active = document.activeElement;
    if (dock == null || (active != null && dock.contains(active))) {
      return;
    }
    if (
      (active instanceof HTMLTextAreaElement ||
        active instanceof HTMLInputElement) &&
      active.value.length > 0
    ) {
      return;
    }
    dock
      .querySelector<HTMLElement>('button, textarea, input')
      ?.focus({ preventScroll: true });
  }, []);

  if (hasNoQuestion) {
    return null;
  }

  return (
    <div
      ref={dockRef}
      role="region"
      aria-label={titleOf(part.input) ?? t('Clarifying questions')}
      css={{
        margin: '0 12px 8px',
        flexShrink: 0,
        border: `1px solid ${colors.divider}`,
        borderRadius: 12,
        backgroundColor: colors.white,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
        padding: '8px 4px 6px',
        // A tall card (6 options, "Other…" open) scrolls inside the dock
        // instead of pushing the chat input off a small screen.
        maxHeight: '45vh',
        overflowY: 'auto',
        overscrollBehaviorY: 'contain',
      }}
    >
      <CardHeader
        title={titleOf(part.input) ?? t('Clarifying questions')}
        colors={colors}
      />
      <InteractiveBody
        questions={questions}
        onResolve={resolve}
        colors={colors}
      />
    </div>
  );
};

// The transcript entry for the call: a status row while the dock is open (or
// the turn still streams), then the answers once it is settled.
export const ClarifyingQuestionsCard = ({
  part,
  isLastAssistant,
  isLoading,
}: {
  part: ToolPart;
  isLastAssistant: boolean;
  isLoading: boolean;
}) => {
  const { colors } = useTheme();
  const { t } = useLocale();
  const mode = clarifyCardMode({ part, isLastAssistant, isLoading });
  const isSettled = mode !== 'interactive' && mode !== 'pending';
  const title = titleOf(part.input) ?? t('Clarifying questions');
  const rows = isSettled ? clarifySummaryRows(part) : [];

  return (
    <div
      css={{
        border: `1px solid ${colors.divider}`,
        borderRadius: 8,
        backgroundColor: colors.white,
        padding: '6px 4px 6px',
      }}
    >
      <CardHeader
        title={title}
        compact={!isSettled}
        colors={colors}
        status={
          mode === 'pending' ? (
            <CircularProgress size={13} sx={{ color: colors.grey500 }} />
          ) : (
            <StatusPill mode={mode} colors={colors} />
          )
        }
      />
      {isSettled && (
        <>
          {rows.map(row => (
            <Typography
              key={row.id}
              variant="body2"
              sx={{ px: 1, py: 0.25, wordBreak: 'break-word' }}
            >
              <span css={{ color: colors.grey600 }}>{row.prompt}</span>
              <span css={{ color: colors.grey400 }}>{' — '}</span>
              <span css={{ color: colors.grey900 }}>{row.response ?? '—'}</span>
            </Typography>
          ))}
          {/* Raw error stays verbatim and screenshot-friendly
              (no-obfuscated-errors). */}
          {mode === 'error' && (
            <pre
              css={{
                margin: '6px 8px 0',
                padding: '8px 10px',
                backgroundColor: colors.grey100,
                borderRadius: 6,
                fontSize: '0.75rem',
                lineHeight: 1.5,
                fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 220,
                overflow: 'auto',
              }}
            >
              {clarifyErrorText(part)}
            </pre>
          )}
        </>
      )}
    </div>
  );
};
